import type { CloudGateway } from "./gateway";
import type { FirebaseCloudOptions } from "./firebase-gateway";

export interface CloudEnvironment {
  readonly [key: string]: unknown;
  VITE_SNAKE_CLOUD_SYNC_ENABLED?: string;
  VITE_FIREBASE_API_KEY?: string;
  VITE_FIREBASE_AUTH_DOMAIN?: string;
  VITE_FIREBASE_DATABASE_URL?: string;
  VITE_FIREBASE_PROJECT_ID?: string;
  VITE_FIREBASE_APP_ID?: string;
  VITE_FIREBASE_STORAGE_BUCKET?: string;
  VITE_FIREBASE_MESSAGING_SENDER_ID?: string;
  VITE_FIREBASE_APPCHECK_SITE_KEY?: string;
}

const trimmed = (value: string | undefined): string | null => {
  const result = value?.trim();
  return result ? result : null;
};

const validDatabaseUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      (url.hostname.endsWith(".firebaseio.com") ||
        url.hostname.endsWith(".firebasedatabase.app")) &&
      !url.username &&
      !url.password &&
      !url.port &&
      (url.pathname === "" || url.pathname === "/") &&
      !url.search &&
      !url.hash
    );
  } catch {
    return false;
  }
};

export const readFirebaseCloudOptions = (
  environment: CloudEnvironment,
): FirebaseCloudOptions | null => {
  if (environment.VITE_SNAKE_CLOUD_SYNC_ENABLED !== "true") return null;
  const apiKey = trimmed(environment.VITE_FIREBASE_API_KEY);
  const authDomain = trimmed(environment.VITE_FIREBASE_AUTH_DOMAIN);
  const databaseURL = trimmed(environment.VITE_FIREBASE_DATABASE_URL);
  const projectId = trimmed(environment.VITE_FIREBASE_PROJECT_ID);
  const appId = trimmed(environment.VITE_FIREBASE_APP_ID);
  const appCheckSiteKey = trimmed(environment.VITE_FIREBASE_APPCHECK_SITE_KEY);
  if (
    !apiKey ||
    !authDomain ||
    !databaseURL ||
    !projectId ||
    !appId ||
    !appCheckSiteKey
  )
    return null;
  if (!validDatabaseUrl(databaseURL)) return null;
  return {
    firebase: {
      apiKey,
      authDomain,
      databaseURL,
      projectId,
      appId,
      storageBucket:
        trimmed(environment.VITE_FIREBASE_STORAGE_BUCKET) ?? undefined,
      messagingSenderId:
        trimmed(environment.VITE_FIREBASE_MESSAGING_SENDER_ID) ?? undefined,
    },
    appCheckSiteKey,
  };
};

export const cloudSyncAvailable = (environment: CloudEnvironment): boolean =>
  readFirebaseCloudOptions(environment) !== null;

export const loadFirebaseCloudGateway = async (
  environment: CloudEnvironment,
): Promise<CloudGateway | null> => {
  const options = readFirebaseCloudOptions(environment);
  if (!options) return null;
  const { createFirebaseCloudGateway } = await import("./firebase-gateway");
  return createFirebaseCloudGateway(options);
};
